import React, { useState, useRef, useEffect } from 'react';

const NormalMultiSelect = ({
  label,
  name,
  value = [],
  onChange,
  options = [],
  placeholder = 'Select options',
  required = false,
  error,
  disabled = false,
  className = '',
  onOpen,
  loading = false,
  onSearch,
  dropDirection = 'auto', // 'auto' | 'down' — 'down' forces dropdown to always open below
}) => {
  // Ensure value is always an array
  const normalizedValue = Array.isArray(value) ? value : [];
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState('top');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // If onSearch is provided (server-side search), don't filter locally
  // Otherwise, filter client-side
  const filteredOptions = onSearch 
    ? options 
    : options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (optionValue) => {
    if (disabled) return;
    
    const newValue = normalizedValue.includes(optionValue)
      ? normalizedValue.filter(v => v !== optionValue)
      : [...normalizedValue, optionValue];
    
    onChange({ target: { name, value: newValue } });
    
    // Clear search term after selection
    setSearchTerm('');
  };

  const handleRemove = (optionValue, e) => {
    e.stopPropagation();
    if (disabled) return;
    
    const newValue = normalizedValue.filter(v => v !== optionValue);
    onChange({ target: { name, value: newValue } });
  };

  const handleSelectAll = () => {
    if (disabled) return;
    
    const allValues = filteredOptions.map(opt => opt.value);
    const allSelected = allValues.every(val => normalizedValue.includes(val));
    
    if (allSelected) {
      // Deselect all filtered options
      const newValue = normalizedValue.filter(v => !allValues.includes(v));
      onChange({ target: { name, value: newValue } });
    } else {
      // Select all filtered options
      const newValue = [...new Set([...normalizedValue, ...allValues])];
      onChange({ target: { name, value: newValue } });
    }
  };

  const selectedOptions = options.filter(opt => normalizedValue.includes(opt.value));

  const handleInputClick = () => {
    if (!disabled) {
      const wasOpen = isOpen;
      setIsOpen(true);
      if (inputRef.current) {
        inputRef.current.focus();
      }
      
      // Fetch data every time dropdown is clicked (when it transitions from closed to open)
      if (!wasOpen) {
        if (onOpen) {
          onOpen();
        }
      }
    }
  };

  const handleInputChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    if (!isOpen) {
      setIsOpen(true);
    }
    
    // Debounced server-side search
    if (onSearch) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        onSearch(newSearchTerm);
      }, 300);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-danger-600 ml-1">*</span>}
        </label>
      )}
      
      <div ref={dropdownRef} className="relative">
        <div
          onClick={handleInputClick}
          className={`min-h-[42px] w-full px-3 py-2 border rounded-md bg-white cursor-pointer transition-colors
            ${error ? 'border-danger-500 focus-within:ring-danger-500' : 'border-gray-300 focus-within:ring-primary-500'}
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'}
            focus-within:ring-2 focus-within:ring-offset-0`}
        >
          <div className="flex flex-wrap gap-1">
            {selectedOptions.length > 0 && (
              selectedOptions.map(option => (
                <span
                  key={option.value}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded text-sm"
                >
                  {option.label}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => handleRemove(option.value, e)}
                      className="hover:text-primary-900"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  )}
                </span>
              ))
            )}
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={() => {
                const wasOpen = isOpen;
                setIsOpen(true);
                if (!wasOpen && onOpen) {
                  onOpen();
                }
              }}
              placeholder={selectedOptions.length === 0 ? placeholder : ''}
              disabled={disabled}
              className="flex-1 min-w-[100px] outline-none bg-transparent text-sm"
            />
          </div>
        </div>

        {isOpen && !disabled && (
          <div className={`absolute z-50 w-full ${dropDirection === 'down' || dropdownPosition !== 'top' ? 'mt-1 top-full' : 'bottom-full mb-1'} bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden`}>
            <div className="overflow-y-auto max-h-60">
              {loading ? (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">Loading...</div>
              ) : filteredOptions.length > 0 ? (
                <>
                  <div
                    onClick={handleSelectAll}
                    className="px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-gray-100 transition-colors border-b border-gray-200 bg-gray-50 font-medium"
                  >
                    <span className="text-sm text-gray-700">Select All</span>
                    {filteredOptions.every(opt => normalizedValue.includes(opt.value)) && (
                      <svg className="ml-auto w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  {filteredOptions.map(option => (
                    <div
                      key={option.value}
                      onClick={() => handleToggle(option.value)}
                      className={`px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-gray-100 transition-colors
                        ${normalizedValue.includes(option.value) ? 'bg-primary-50 text-primary-700 font-medium' : ''}`}
                    >
                      <span className="text-sm">{option.label}</span>
                      {normalizedValue.includes(option.value) && (
                        <svg className="ml-auto w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">No options found</div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-danger-600">{error}</p>
      )}
    </div>
  );
};

export default NormalMultiSelect;
